// The proposal card for a strategy (StrategyProposalBody, spec §13.15):
// the phases, the goals with their priority, the weekly hours, the key
// sessions per phase, the coach's notes (body.findings), and "How I planned
// this" (the decision record; always closed until tapped: open by default it
// buried a beginner's first plan under ~40 bullets, persona QA 2026-10-25), with
// Accept · Not now · Why?. A block or week proposal says plainly that it
// changes those dates only. Kept expanded until answered; then one line
// with the outcome (a superseded one says it was replaced, no actions).
// An accept the coach answered in words (`reply`: updating, kept_current)
// shows them on the card (ProposalStatus) instead of the buttons it no longer offers.
// The card says what Accept replaces and what stays (body.replaces), and one
// the calendar changed under (body.stale) says so and offers Refresh, not Accept.
// How far ahead the plan goes (body.horizon.line, written in words by the
// backend) sits under the sessions line; older proposals have none.
import React, { useState } from 'react';
import { AlertTriangle, CalendarDays, Check, ChevronDown, ChevronRight, RefreshCw, Star } from 'lucide-react';
import { Button } from '../ds';
import { CardActions, CoachCard, Eyebrow, WhyLink } from './coachCard';
import { cn } from '../../lib/utils';
import { commands } from '../../v2/commands';
import { hoursLabel, scopeNote } from '../../v2/timeline';
import { proposalAnswers, proposalOutcome, staleNote } from '../../v2/threadView';
import PhaseStrip from './PhaseStrip';
import ProposalStatus from './ProposalStatus';
import { answersAfter } from '../../v2/failures';
import { keySessionsByPhase } from '../../v2/keySessions';

const RECORD = [
  ['looked_at', 'What I looked at'],
  ['decided', 'What I decided'],
  ['kept', 'What I kept'],
  ['considered', "What I considered but didn't do"],
];

function DecisionRecord({ record }) {
  const [open, setOpen] = useState(false);
  const parts = RECORD.filter(([k]) => record?.[k]?.length);
  if (!parts.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex min-h-[28px] items-center gap-1 type-caption text-text-tertiary hover:text-text-primary">
        How I planned this {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {open && (
        <div className="mt-1 space-y-2">
          {parts.map(([k, label]) => (
            <div key={k}>
              <Eyebrow tone="muted">{label}</Eyebrow>
              <ul className="ml-4 mt-0.5 list-disc type-caption text-text-body">
                {record[k].map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PriorityBadge = ({ priority }) => (priority ? (
  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold',
    priority === 'A' ? 'bg-bg-raised text-zone-threshold' : priority === 'B' ? 'bg-accent-tint text-accent-default' : 'bg-bg-raised text-text-secondary')}>
    {priority}
  </span>
) : null);

/** `readOnly` (a coach viewing the athlete): the card without Accept, Not now or Why?. */
export default function StrategyProposalCard({ item, onAct, onWhy, busy = false, today = null, races = [], readOnly = false, reply = null }) {
  const b = item.body || {};
  const outcome = proposalOutcome(item);
  if (outcome) {
    // Answered: one line with the outcome.
    return (
      <CoachCard role="article" aria-label={`Plan proposal, ${outcome}`} className="flex-row flex-wrap items-center gap-x-2 gap-y-1">
        <span className="type-label text-text-primary">{b.title || 'Proposed plan'}</span>
        <span className="inline-flex items-center gap-1 type-caption text-text-tertiary"><Check size={12} /> {outcome}</span>
      </CoachCard>
    );
  }
  // What Accept replaces and what stays (backend: body.replaces); older cards: the block-only note
  const scoped = (typeof b.replaces === 'string' && b.replaces.trim()) || scopeNote(b);
  const stale = staleNote(item);
  const hours = hoursLabel(b.weekly_hours);
  const timeline = b.timeline || [];
  const phaseName = Object.fromEntries(timeline.map((p) => [String(p.id), p.name]));
  const keySessions = keySessionsByPhase(b);   // the planner's ids in words ("full_body" → "Full-body strength")
  const findings = (Array.isArray(b.findings) ? b.findings : []).filter((f) => typeof f === 'string' && f.trim());
  const horizon = typeof b.horizon?.line === 'string' ? b.horizon.line.trim() : '';
  const after = answersAfter(reply);
  const actions = proposalAnswers(item).filter((a) => !after || after.includes(a));
  return (
    <CoachCard tone="proposal" role="article" aria-label="Plan proposal">
      <Eyebrow>Proposed plan</Eyebrow>
      {b.title && <p className="m-0 text-[20px] font-semibold leading-[1.2] tracking-[-0.01em] text-text-primary">{b.title}</p>}
      {b.reason && <p className="m-0 type-body-small text-text-secondary">{b.reason}</p>}
      {scoped && (
        <p className={cn('m-0 rounded-chip bg-bg-surface px-2.5 py-1.5 type-caption',
          scopeNote(b) ? 'text-zone-threshold' : 'text-text-body')}>{scoped}</p>
      )}

      {timeline.length > 0 && <PhaseStrip phases={timeline} races={races} today={today} draft compact />}

      {b.goals?.length > 0 && (
        <ul className="m-0 list-none space-y-1 p-0">
          {b.goals.map((g, i) => (
            <li key={i} className="flex items-center gap-1.5 type-caption text-text-primary">
              {g.priority === 'A' ? <Star size={12} className="fill-zone-threshold text-zone-threshold" /> : <span className="w-3" />}
              <span className="font-medium">{g.title}</span>
              <PriorityBadge priority={g.priority} />
            </li>
          ))}
        </ul>
      )}

      {hours && <p className="m-0 type-caption text-text-secondary">{hours}</p>}

      {keySessions.length > 0 && (
        <div>
          <Eyebrow tone="muted">Key sessions</Eyebrow>
          <ul className="m-0 mt-0.5 list-none space-y-0.5 p-0 type-caption text-text-body">
            {keySessions.map(([pid, list]) => (
              <li key={pid}><span className="font-medium text-text-primary">{phaseName[pid] || 'Phase'}:</span> {list.join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      {(b.sessions_written > 0 || b.replaces_sessions > 0) && (
        <p className="m-0 type-caption text-text-tertiary">
          {b.sessions_written > 0 && `${b.sessions_written} sessions written for the next two weeks`}
          {b.sessions_written > 0 && b.replaces_sessions > 0 && ' · '}
          {b.replaces_sessions > 0 && `replaces ${b.replaces_sessions} in your current plan`}
        </p>
      )}

      {horizon && (
        <p className="m-0 flex items-start gap-1.5 type-caption text-text-tertiary">
          <CalendarDays size={12} className="mt-0.5 shrink-0" aria-hidden="true" /> {horizon}
        </p>
      )}

      {findings.length > 0 && (
        <div>
          <Eyebrow tone="muted">Notes from your coach</Eyebrow>
          <ul className="m-0 mt-0.5 ml-4 list-disc type-caption text-text-body">
            {findings.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
      )}

      <DecisionRecord record={b.decision_record} />

      {stale && (
        <p className="m-0 flex items-start gap-1.5 rounded-chip bg-bg-surface px-2.5 py-1.5 type-caption text-zone-threshold" role="status">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden="true" /> {stale}
        </p>
      )}

      <ProposalStatus outcome={reply} />

      {!readOnly && (
      <CardActions>
        {actions.includes('refresh') && (
          <Button size="small" disabled={busy} icon={<RefreshCw size={13} />} onClick={() => onAct?.(commands.refreshProposal(item))}>Refresh</Button>
        )}
        {actions.includes('accept') && (
          <Button size="small" disabled={busy} onClick={() => onAct?.(commands.acceptProposal(item))}>Accept</Button>
        )}
        {actions.includes('dismiss') && (
          <Button size="small" kind="secondary" disabled={busy} onClick={() => onAct?.(commands.dismissProposal(item))}>Not now</Button>
        )}
        <WhyLink onClick={() => onWhy?.(item)} />
      </CardActions>
      )}
    </CoachCard>
  );
}
